import { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import { skipToken } from "@reduxjs/toolkit/query"
import dayjs from "dayjs"
import styled from "styled-components"

import { useGetCustomerCallsAnalyticsQuery, useGetMonthlyEngagementSummaryQuery } from "store/api/engagementApi"
import { FeatureFlag, isFeatureEnabled } from "store/featureFlagsSlice"
import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"
import { ReactComponent as IconArrowDownTray } from "assets/svg/export-icon.svg"
import { ReactComponent as IconDocumentDuplicate } from "assets/svg/icon-document-duplicate.svg"
import defaultLogo from "assets/svg/logo-blue.svg"

import { useMonthlyEngagementPdfDownloader } from "hooks/useMonthlyEngagementPdfDownloader"
import { PartnerLevelAnalytics } from "components/common/MonthlyEngagementPage/PartnerLevelAnalytics"
import { Spinner } from "components/common/Spinner"

import { CustomerCallsSection } from "./CustomerCallsSection"
import { MobileAppEngagement } from "./MobileAppEngagement"
import { getActiveMonthlyEngagementSectionId, monthlyEngagementSections } from "./navigation"
import { buildMonthlyEngagementReportData } from "./reportData"
import { ReportSummary } from "./ReportSummary"
import {
  Page,
  ReportBody,
  ReportSectionAnchor,
  ReportSectionNavigation,
  ReportSectionNavigationShell,
  ReportSectionNavLink,
  ReportShell,
  ReportStickyHeader,
  ReportToolbar,
  ReportToolbarActions,
  ReportToolbarContent,
  ReportToolbarDivider,
  ReportToolbarLeft,
  ReportToolbarLogo,
  ReportToolbarSubtitle,
  ReportToolbarTitle,
  ReportToolbarTitleBlock,
  SummaryActionButton,
  SummaryContractor,
  SummaryCover,
  SummaryCoverBody,
  SummaryCoverContent,
  SummaryCoverHeader,
  SummaryHeadline,
  SummaryLogoFallback,
  SummaryLogoImage,
  SummaryPill,
  SummaryPillDot,
  SummaryPills,
  SummaryPillText,
} from "./styled"
import { ThermostatEngagement } from "./ThermostatEngagement"

const LoadingState = styled.div`
  width: 100%;
  min-height: 100vh;
  background: var(--surface-3);
  display: flex;
  align-items: center;
  justify-content: center;
`

const monthlyEngagementServerErrorToastId = "monthly-engagement-report-server-error"
const monthlyEngagementServerErrorMessage = "Monthly engagement report data is unavailable."

type MonthlyEngagementQueryParams = {
  reporting_period_from: string
  reporting_period_to: string
}

type MonthlyEngagementApiParams = MonthlyEngagementQueryParams & {
  contractor_id: number
  useMockData: boolean
}

type CustomerCallsApiParams = MonthlyEngagementApiParams & {
  page: number
  per_page: number
  outcome: "all" | "answered" | "missed"
}

const decodeMonthlyEngagementParams = (search: string): MonthlyEngagementQueryParams | undefined => {
  const searchParams = new URLSearchParams(search)
  const from = searchParams.get("reporting_period_from")
  const to = searchParams.get("reporting_period_to")
  if (!from || !to) return undefined

  return {
    reporting_period_from: from,
    reporting_period_to: to,
  }
}

const isMockDataRequested = (search: string) => new URLSearchParams(search).get("mock") === "true"

const MonthlyEngagementPage = () => {
  const { hash, pathname, search } = useLocation()
  const navigate = useNavigate()
  const reportingPeriodType = new URLSearchParams(search).get("reporting_period_type") ?? undefined
  const engagementParams = new URLSearchParams(search)
  const callLogPage = Math.max(1, Number(engagementParams.get("page")) || 1)
  const callLogPageSize = Math.max(1, Number(engagementParams.get("perPage")) || 15)
  const callLogOutcome = (engagementParams.get("outcome") as "all" | "answered" | "missed" | null) ?? "all"
  useEffect(() => {
    const params = new URLSearchParams(search)
    if (params.get("reporting_period_from") && params.get("reporting_period_to")) return

    const latestCompletedMonth = dayjs().startOf("month").subtract(1, "month")
    if (!params.get("reporting_period_from")) {
      params.set("reporting_period_from", latestCompletedMonth.startOf("month").format("YYYY-MM-DD"))
    }
    if (!params.get("reporting_period_to")) {
      params.set("reporting_period_to", latestCompletedMonth.endOf("month").format("YYYY-MM-DD"))
    }
    navigate({ pathname, search: `?${params.toString()}`, hash }, { replace: true })
  }, [hash, navigate, pathname, search])
  const { downloadMonthlyEngagementPdf, isDownloadingMonthlyEngagementPdf } = useMonthlyEngagementPdfDownloader()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const userLogo = useAppSelector(userLogoSelector)
  const contractor = userProfile?.contractor
  const monthlyEngagementQueryParams = useMemo(() => decodeMonthlyEngagementParams(search), [search])
  const useMockData = useMemo(() => isMockDataRequested(search), [search])
  const monthlyEngagementApiParams = useMemo<MonthlyEngagementApiParams | typeof skipToken>(() => {
    if (!monthlyEngagementQueryParams || !contractor?.id) return skipToken

    return {
      ...monthlyEngagementQueryParams,
      contractor_id: contractor.id,
      useMockData,
    }
  }, [contractor?.id, monthlyEngagementQueryParams, useMockData])
  const customerCallsApiParams = useMemo<CustomerCallsApiParams | typeof skipToken>(() => {
    if (monthlyEngagementApiParams === skipToken) return skipToken

    return {
      ...monthlyEngagementApiParams,
      page: callLogPage,
      per_page: callLogPageSize,
      outcome: callLogOutcome,
    }
  }, [callLogOutcome, callLogPage, callLogPageSize, monthlyEngagementApiParams])
  const isPartnerAnalyticsEnabled = useAppSelector(
    isFeatureEnabled(FeatureFlag.MonthlyEngagementReportPartnersAnalytics),
  )
  const isCustomerCallsEnabled = useAppSelector(isFeatureEnabled(FeatureFlag.MonthlyEngagementReportCustomerCalls))
  const currentTheme = useAppSelector(contractorThemeSelector)
  const { data: summaryData, isError, isLoading } = useGetMonthlyEngagementSummaryQuery(monthlyEngagementApiParams)
  const { data: customerCallsData } = useGetCustomerCallsAnalyticsQuery(
    isCustomerCallsEnabled ? customerCallsApiParams : skipToken,
  )
  const reportData = useMemo(
    () =>
      buildMonthlyEngagementReportData({
        summary: summaryData,
        customerCalls: customerCallsData,
        reportingPeriodFrom: monthlyEngagementQueryParams?.reporting_period_from,
        reportingPeriodTo: monthlyEngagementQueryParams?.reporting_period_to,
        reportingPeriodType,
      }),
    [customerCallsData, monthlyEngagementQueryParams, reportingPeriodType, summaryData],
  )
  const shouldRenderReportContent = !isError
  const shouldHideSummaryZeroValues = isError
  const canUseCustomTheme =
    contractor?.subscription_plan === SubscriptionPlan.E_PLUS || Boolean(userProfile?.enterprise)
  const backgroundGradient = canUseCustomTheme
    ? currentTheme.backgroundGradient
    : ContractorThemes.default.backgroundGradient
  const logoSrc = userLogo ? `${userLogo}?cors=logo` : defaultLogo
  const visibleSections = useMemo(
    () =>
      monthlyEngagementSections.filter(section => {
        const enablePartners = section.id !== "partner-level-analytics" || isPartnerAnalyticsEnabled
        const enableCustomerCalls = section.id !== "customer-calls" || isCustomerCallsEnabled
        return enablePartners && enableCustomerCalls
      }),
    [isCustomerCallsEnabled, isPartnerAnalyticsEnabled],
  )
  const [activeSectionId, setActiveSectionId] = useState<(typeof monthlyEngagementSections)[number]["id"]>(
    visibleSections[0]?.id ?? "engagement-summary",
  )

  useEffect(() => {
    if (!visibleSections.some(section => section.id === activeSectionId)) {
      setActiveSectionId(visibleSections[0]?.id ?? "engagement-summary")
    }
  }, [activeSectionId, visibleSections])

  useEffect(() => {
    if (isError) {
      toast.error(monthlyEngagementServerErrorMessage, {
        toastId: monthlyEngagementServerErrorToastId,
      })
    }
  }, [isError])

  useEffect(() => {
    if (hash) {
      const matchedSection = visibleSections.find(section => `#${section.id}` === hash)

      if (matchedSection) {
        setActiveSectionId(matchedSection.id)
      }
    }
  }, [hash, visibleSections])

  useEffect(() => {
    const updateActiveSection = () => {
      const offset = 156
      const nextActiveSectionId = getActiveMonthlyEngagementSectionId(
        visibleSections.map(section => section.id),
        sectionId => document.getElementById(sectionId)?.getBoundingClientRect().top,
        offset,
      )

      setActiveSectionId(currentSectionId =>
        currentSectionId === nextActiveSectionId ? currentSectionId : nextActiveSectionId,
      )
    }

    updateActiveSection()
    window.addEventListener("scroll", updateActiveSection, { passive: true })
    window.addEventListener("resize", updateActiveSection)

    return () => {
      window.removeEventListener("scroll", updateActiveSection)
      window.removeEventListener("resize", updateActiveSection)
    }
  }, [visibleSections])

  const handleCopyLink = async () => {
    try {
      const reportUrl = new URL("/engagement-report", window.location.origin)

      if (monthlyEngagementQueryParams?.reporting_period_from) {
        reportUrl.searchParams.set("reporting_period_from", monthlyEngagementQueryParams.reporting_period_from)
      }

      if (monthlyEngagementQueryParams?.reporting_period_to) {
        reportUrl.searchParams.set("reporting_period_to", monthlyEngagementQueryParams.reporting_period_to)
      }
      if (useMockData) {
        reportUrl.searchParams.set("mock", "true")
      }
      await navigator.clipboard.writeText(reportUrl.toString())
      toast.success("Link copied to clipboard")
    } catch {
      toast.error("Failed to copy link")
    }
  }

  const handleDownloadPdf = async () => {
    if (monthlyEngagementApiParams === skipToken) {
      return
    }

    await downloadMonthlyEngagementPdf({
      ...monthlyEngagementApiParams,
      reporting_period_type: reportingPeriodType,
    })
  }

  if (monthlyEngagementApiParams === skipToken || isLoading || (!summaryData && !isError)) {
    return (
      <LoadingState role="status" aria-label="Loading engagement report">
        <Spinner />
      </LoadingState>
    )
  }

  return (
    <ReportShell $backgroundGradient={backgroundGradient}>
      <ReportStickyHeader>
        <ReportToolbar>
          <ReportToolbarContent>
            <ReportToolbarLeft>
              <ReportToolbarLogo>
                <img src={logoSrc} alt="logo" />
              </ReportToolbarLogo>
              <ReportToolbarDivider />
              <ReportToolbarTitleBlock>
                <ReportToolbarTitle>Engagement Report</ReportToolbarTitle>
                {shouldRenderReportContent ? (
                  <ReportToolbarSubtitle>{reportData.reportingPeriod}</ReportToolbarSubtitle>
                ) : null}
              </ReportToolbarTitleBlock>
            </ReportToolbarLeft>
            {shouldRenderReportContent ? (
              <ReportToolbarActions>
                <SummaryActionButton type="button" $variant="secondary" onClick={handleCopyLink}>
                  <IconDocumentDuplicate />
                  Copy link
                </SummaryActionButton>

                <SummaryActionButton
                  type="button"
                  $variant="primary"
                  disabled={isDownloadingMonthlyEngagementPdf}
                  onClick={handleDownloadPdf}
                >
                  <IconArrowDownTray />
                  {isDownloadingMonthlyEngagementPdf ? "Generating..." : "Download PDF"}
                </SummaryActionButton>
              </ReportToolbarActions>
            ) : null}
          </ReportToolbarContent>
        </ReportToolbar>
        {shouldRenderReportContent ? (
          <ReportSectionNavigationShell>
            <ReportSectionNavigation aria-label="Monthly engagement report sections">
              {visibleSections.map(section => (
                <ReportSectionNavLink
                  key={section.id}
                  href={`#${section.id}`}
                  $active={activeSectionId === section.id}
                  onClick={() => setActiveSectionId(section.id)}
                >
                  {section.label}
                </ReportSectionNavLink>
              ))}
            </ReportSectionNavigation>
          </ReportSectionNavigationShell>
        ) : null}
      </ReportStickyHeader>
      <ReportBody>
        <Page>
          <SummaryCover id="engagement-summary" $backgroundGradient={backgroundGradient}>
            <SummaryCoverContent>
              <SummaryCoverHeader>
                <SummaryCoverBody>
                  {logoSrc ? (
                    <SummaryLogoImage src={logoSrc} alt={`${reportData.contractorName} logo`} />
                  ) : (
                    <SummaryLogoFallback>{reportData.contractorName}</SummaryLogoFallback>
                  )}
                  <SummaryHeadline>Your engagement report</SummaryHeadline>
                  <SummaryContractor>{reportData.contractorName}</SummaryContractor>
                  {shouldRenderReportContent ? (
                    <SummaryPills>
                      <SummaryPill>
                        <SummaryPillDot />
                        <SummaryPillText>{reportData.reportingPeriod}</SummaryPillText>
                      </SummaryPill>
                      <SummaryPill>
                        <SummaryPillText>{reportData.planLabel}</SummaryPillText>
                      </SummaryPill>
                    </SummaryPills>
                  ) : null}
                </SummaryCoverBody>
              </SummaryCoverHeader>
              <ReportSummary
                executiveMetrics={reportData.executiveMetrics}
                growthMetrics={reportData.growthMetrics}
                periodLabels={reportData.periodLabels}
                hideZeroValues={shouldHideSummaryZeroValues}
                isTitlePage
              />
            </SummaryCoverContent>
          </SummaryCover>
          {shouldRenderReportContent && (
            <>
              <ReportSectionAnchor id="thermostat-engagement">
                <ThermostatEngagement
                  kpis={reportData.thermostatKpis}
                  weeklyTrend={reportData.thermostatWeeklyTrend}
                  topDevices={reportData.topDevices}
                  periodLabels={reportData.periodLabels}
                />
              </ReportSectionAnchor>
              <ReportSectionAnchor id="mobile-app-engagement">
                <MobileAppEngagement
                  kpiGroups={reportData.mobileKpiGroups}
                  weeklyTrend={reportData.mobileWeeklyTrend}
                  periodLabels={reportData.periodLabels}
                />
              </ReportSectionAnchor>
              {isCustomerCallsEnabled && (
                <ReportSectionAnchor id="customer-calls">
                  <CustomerCallsSection
                    calls={reportData.customerCalls}
                    periodLabels={reportData.periodLabels}
                    filter={callLogOutcome}
                    onFilterChange={value => {
                      const params = new URLSearchParams(search)
                      params.set("outcome", value)
                      params.set("page", "1")
                      navigate({ pathname, search: `?${params.toString()}`, hash }, { replace: true })
                    }}
                    pagination={customerCallsData?.call_log?.meta}
                  />
                </ReportSectionAnchor>
              )}
              {isPartnerAnalyticsEnabled && reportData.partnerAnalytics.length > 0 && (
                <ReportSectionAnchor id="partner-level-analytics">
                  <PartnerLevelAnalytics partners={reportData.partnerAnalytics} />
                </ReportSectionAnchor>
              )}
            </>
          )}
        </Page>
      </ReportBody>
    </ReportShell>
  )
}

export default MonthlyEngagementPage
